'use client';

import React, { Component, ReactNode } from 'react';
import { AlertCircle, RefreshCw, Home, Wifi, WifiOff } from 'lucide-react';
import { cn } from '@/lib/utils';

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: any;
  errorId: string;
  retryCount: number;
  isHydrationError: boolean;
  autoRefreshCountdown: number;
}

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: any) => void;
  maxRetries?: number;
  level?: 'app' | 'page' | 'component';
  context?: string;
}

export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  private retryTimeoutId: NodeJS.Timeout | null = null;
  private countdownIntervalId: NodeJS.Timeout | null = null;

  constructor(props: ErrorBoundaryProps) {
    super(props);
    
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: '',
      retryCount: 0,
      isHydrationError: false,
      autoRefreshCountdown: 0
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    // Générer un ID unique pour cette erreur
    const errorId = `err_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    
    // Détecter les erreurs d'hydratation (React Error #310)
    const isHydrationError = ErrorBoundary.detectHydrationError(error);
    
    return {
      hasError: true,
      error,
      errorId,
      isHydrationError,
      // Initialiser countdown à 10 secondes pour auto-refresh si erreur d'hydratation
      autoRefreshCountdown: isHydrationError ? 10 : 0
    };
  }

  // 🔍 DÉTECTION SPÉCIFIQUE DES ERREURS D'HYDRATATION
  private static detectHydrationError(error: Error): boolean {
    const hydrationPatterns = [
      /Text content does not match server-rendered HTML/i,
      /Hydration failed/i,
      /There was an error while hydrating/i,
      /The server HTML/i,
      /Cannot read propert(y|ies) of null/i, // Souvent lié à hydratation
      /Expected server HTML to contain/i,
      /Warning.*hydration/i
    ];

    const errorMessage = error.message || '';
    const errorStack = error.stack || '';
    const fullErrorText = `${errorMessage} ${errorStack}`;

    return hydrationPatterns.some(pattern => pattern.test(fullErrorText));
  }

  componentDidCatch(error: Error, errorInfo: any) {
    // 🔍 LOGS DÉTAILLÉS POUR DIAGNOSTIC React Error #310
    console.group(`🔴 [ErrorBoundary ${this.props.context || 'Unknown'}] Error caught`);
    console.error('Error Object:', error);
    console.error('Error Message:', error.message);
    console.error('Error Stack:', error.stack);
    console.error('Error Name:', error.name);
    console.error('Component Stack:', errorInfo?.componentStack);
    console.error('Error Info Full:', errorInfo);
    console.error('Error ID:', this.state.errorId);
    console.error('Level:', this.props.level);
    console.error('Retry Count:', this.state.retryCount);
    console.error('Is Hydration Error:', this.state.isHydrationError);
    
    // 🔍 Informations supplémentaires pour debugging
    console.error('Window Location:', typeof window !== 'undefined' ? window.location.href : 'N/A');
    console.error('User Agent:', typeof navigator !== 'undefined' ? navigator.userAgent : 'N/A');
    console.error('Timestamp:', new Date().toISOString());
    
    // 🔍 Essayer d'extraire plus d'infos de l'erreur minifiée
    if (error.message.includes('Minified React error')) {
      const errorCodeMatch = error.message.match(/#(\d+)/);
      if (errorCodeMatch) {
        console.error('🔴 React Error Code:', errorCodeMatch[1]);
        console.error('🔗 Full docs:', `https://react.dev/errors/${errorCodeMatch[1]}`);
      }
    }
    
    console.groupEnd();

    this.setState({ errorInfo });

    // ⚠️ GESTION SPÉCIALE POUR ERREURS D'HYDRATATION
    if (this.state.isHydrationError) {
      console.warn(`[ErrorBoundary] 🔄 Hydration error detected! Auto-refresh in ${this.state.autoRefreshCountdown}s`);
      this.startAutoRefreshCountdown();
    }

    // Appeler le callback d'erreur si fourni
    this.props.onError?.(error, errorInfo);

    // Enregistrer l'erreur pour monitoring (peut être étendu avec Sentry, etc.)
    this.logError(error, errorInfo);
  }

  // ⏱️ COUNTDOWN AUTO-REFRESH POUR ERREURS D'HYDRATATION
  private startAutoRefreshCountdown = () => {
    if (this.countdownIntervalId) {
      clearInterval(this.countdownIntervalId);
    }

    this.countdownIntervalId = setInterval(() => {
      this.setState(prevState => {
        const newCountdown = prevState.autoRefreshCountdown - 1;
        
        if (newCountdown <= 0) {
          // Auto-refresh de la page pour résoudre l'erreur d'hydratation
          console.log('[ErrorBoundary] 🔄 Auto-refreshing page to resolve hydration error...');
          if (typeof window !== 'undefined') {
            window.location.reload();
          }
          return prevState;
        }
        
        return { ...prevState, autoRefreshCountdown: newCountdown };
      });
    }, 1000);
  };

  private logError = (error: Error, errorInfo: any) => {
    // Log détaillé pour debugging
    const errorData = {
      message: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
      url: typeof window !== 'undefined' ? window.location.href : 'unknown',
      userAgent: typeof window !== 'undefined' ? window.navigator.userAgent : 'unknown',
      level: this.props.level,
      context: this.props.context,
      errorId: this.state.errorId,
      retryCount: this.state.retryCount,
      isHydrationError: this.state.isHydrationError,
      autoRefreshCountdown: this.state.autoRefreshCountdown
    };

    // En dev, log complet en console avec indication spéciale pour hydratation
    if (process.env.NODE_ENV === 'development') {
      const errorType = this.state.isHydrationError ? '💧 HYDRATION ERROR' : '🚨 Error Boundary';
      console.group(`${errorType} [${this.props.level}] - ${this.props.context}`);
      console.error('Error:', error);
      console.error('Error Info:', errorInfo);
      console.table(errorData);
      
      if (this.state.isHydrationError) {
        console.warn('🔄 This is a hydration error - page will auto-refresh to resolve the issue');
      }
      
      console.groupEnd();
    }

    // TODO: Envoyer à un service de monitoring en production
    // if (process.env.NODE_ENV === 'production') {
    //   sendToErrorMonitoring(errorData);
    // }
  };

  private handleRetry = () => {
    const maxRetries = this.props.maxRetries || 3;
    
    if (this.state.retryCount < maxRetries) {
      console.log(`[ErrorBoundary] Retrying... (${this.state.retryCount + 1}/${maxRetries})`);
      
      // Arrêter le countdown auto-refresh
      this.stopAutoRefreshCountdown();
      
      this.setState(prevState => ({
        hasError: false,
        error: null,
        errorInfo: null,
        retryCount: prevState.retryCount + 1,
        isHydrationError: false,
        autoRefreshCountdown: 0
      }));
    }
  };

  private handleReset = () => {
    console.log('[ErrorBoundary] Resetting error state');
    
    // Arrêter le countdown auto-refresh
    this.stopAutoRefreshCountdown();
    
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
      isHydrationError: false,
      autoRefreshCountdown: 0
    });
  };

  private stopAutoRefreshCountdown = () => {
    if (this.countdownIntervalId) {
      clearInterval(this.countdownIntervalId);
      this.countdownIntervalId = null;
    }
  };

  private handleGoHome = () => {
    if (typeof window !== 'undefined') {
      window.location.href = '/';
    }
  };

  componentWillUnmount() {
    // Cleanup des timers
    if (this.retryTimeoutId) {
      clearTimeout(this.retryTimeoutId);
    }
    if (this.countdownIntervalId) {
      clearInterval(this.countdownIntervalId);
    }
  }

  private renderErrorUI = () => {
    const { level = 'component', context } = this.props;
    const { error, retryCount, errorId, isHydrationError, autoRefreshCountdown } = this.state;
    const maxRetries = this.props.maxRetries || 3;
    const canRetry = retryCount < maxRetries;

    // 🔄 UI SPÉCIAL POUR ERREURS D'HYDRATATION
    if (isHydrationError) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-background to-muted flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-card rounded-lg shadow-lg p-6 text-center">
            <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <RefreshCw className={cn("w-8 h-8 text-primary", autoRefreshCountdown > 0 && "animate-spin")} />
            </div>
            
            <h1 className="text-xl font-bold text-foreground mb-2">
              Hydration Error Detected
            </h1>
            
            <p className="text-muted-foreground mb-4">
              Une erreur d'hydratation React s'est produite. La page va se recharger automatiquement pour résoudre le problème.
            </p>
            
            {autoRefreshCountdown > 0 ? (
              <div className="bg-primary/10 rounded-lg p-4 mb-4">
                <div className="text-2xl font-bold text-primary mb-1">
                  {autoRefreshCountdown}
                </div>
                <p className="text-sm text-primary">
                  Rechargement automatique dans...
                </p>
              </div>
            ) : (
              <div className="bg-muted rounded-lg p-4 mb-4">
                <p className="text-sm text-foreground/80">
                  Rechargement en cours...
                </p>
              </div>
            )}

            <div className="bg-muted rounded p-3 mb-4 text-sm text-left">
              <p className="text-muted-foreground mb-1">Error ID: {errorId}</p>
              <p className="text-foreground/80 font-mono text-xs break-all">
                {error?.message || 'Hydration mismatch'}
              </p>
            </div>

            <div className="space-y-2">
              <button
                onClick={() => window.location.reload()}
                className="w-full bg-primary text-primary-foreground py-2 px-4 rounded hover:bg-primary/90 flex items-center justify-center gap-2"
              >
                <RefreshCw className="w-4 h-4" />
                Recharger maintenant
              </button>
              
              <button
                onClick={this.stopAutoRefreshCountdown}
                className="w-full bg-secondary text-secondary-foreground py-2 px-4 rounded hover:bg-secondary/80 text-sm"
              >
                Annuler auto-refresh
              </button>
              
              {process.env.NODE_ENV === 'development' && (
                <details className="text-xs text-left mt-3">
                  <summary className="cursor-pointer text-muted-foreground hover:text-foreground">
                    Détails techniques (dev)
                  </summary>
                  <pre className="mt-2 bg-muted p-2 rounded text-xs overflow-auto">
                    {error?.stack}
                  </pre>
                </details>
              )}
            </div>
          </div>
        </div>
      );
    }

    // Différents niveaux d'erreur avec différents UIs
    switch (level) {
      case 'app':
        return (
          <div className="min-h-screen bg-gradient-to-br from-background to-muted flex items-center justify-center p-4">
            <div className="max-w-md w-full bg-card rounded-lg shadow-lg p-6 text-center">
              <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertCircle className="w-8 h-8 text-destructive" />
              </div>
              
              <h1 className="text-xl font-bold text-foreground mb-2">
                Application Error
              </h1>
              
              <p className="text-muted-foreground mb-4">
                Une erreur inattendue s'est produite. Nous nous excusons pour la gêne occasionnée.
              </p>
              
              <div className="bg-muted rounded p-3 mb-4 text-sm text-left">
                <p className="text-muted-foreground mb-1">Error ID: {errorId}</p>
                <p className="text-foreground/80 font-mono text-xs break-all">
                  {error?.message || 'Unknown error'}
                </p>
              </div>

              <div className="space-y-2">
                {canRetry && (
                  <button
                    onClick={this.handleRetry}
                    className="w-full bg-primary text-primary-foreground py-2 px-4 rounded hover:bg-primary/90 flex items-center justify-center gap-2"
                  >
                    <RefreshCw className="w-4 h-4" />
                    Retry ({retryCount}/{maxRetries})
                  </button>
                )}
                
                <button
                  onClick={this.handleGoHome}
                  className="w-full bg-secondary text-secondary-foreground py-2 px-4 rounded hover:bg-secondary/80 flex items-center justify-center gap-2"
                >
                  <Home className="w-4 h-4" />
                  Go to Home
                </button>
              </div>
            </div>
          </div>
        );

      case 'page':
        return (
          <div className="flex-1 flex items-center justify-center p-8 bg-muted">
            <div className="max-w-sm w-full bg-card rounded-lg shadow p-6 text-center">
              <div className="w-12 h-12 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-3">
                <AlertCircle className="w-6 h-6 text-destructive" />
              </div>
              
              <h2 className="text-lg font-semibold text-foreground mb-2">
                Page Error
              </h2>
              
              <p className="text-sm text-muted-foreground mb-4">
                Cette page a rencontré un problème.
              </p>

              <p className="text-xs text-muted-foreground mb-3 font-mono">
                {context && `Context: ${context}`}
              </p>

              <div className="space-y-2">
                {canRetry && (
                  <button
                    onClick={this.handleRetry}
                    className="w-full bg-primary text-primary-foreground py-2 px-3 rounded text-sm hover:bg-primary/90 flex items-center justify-center gap-2"
                  >
                    <RefreshCw className="w-3 h-3" />
                    Retry
                  </button>
                )}
                
                <button
                  onClick={this.handleReset}
                  className="w-full bg-secondary text-secondary-foreground py-2 px-3 rounded text-sm hover:bg-secondary/80"
                >
                  Reset
                </button>
              </div>
            </div>
          </div>
        );

      case 'component':
      default:
        return (
          <div className="border border-destructive/20 bg-destructive/10 rounded-lg p-4 m-2">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-destructive mt-0.5 flex-shrink-0" />
              <div className="flex-1">
                <h3 className="text-sm font-medium text-destructive mb-1">
                  Component Error
                </h3>
                <p className="text-sm text-destructive mb-2">
                  {context ? `${context} failed to load.` : 'Component failed to load.'}
                </p>
                
                {process.env.NODE_ENV === 'development' && (
                  <details className="text-xs text-destructive mb-2">
                    <summary className="cursor-pointer">Error Details</summary>
                    <pre className="mt-1 whitespace-pre-wrap break-all">
                      {error?.message}
                    </pre>
                  </details>
                )}

                {canRetry && (
                  <button
                    onClick={this.handleRetry}
                    className="inline-flex items-center gap-1 text-xs bg-destructive text-destructive-foreground px-2 py-1 rounded hover:bg-destructive/90"
                  >
                    <RefreshCw className="w-3 h-3" />
                    Retry ({retryCount}/{maxRetries})
                  </button>
                )}
              </div>
            </div>
          </div>
        );
    }
  };

  render() {
    if (this.state.hasError) {
      // Utiliser le fallback personnalisé si fourni
      if (this.props.fallback) {
        return this.props.fallback;
      }
      
      // Sinon utiliser l'UI d'erreur par défaut
      return this.renderErrorUI();
    }

    return this.props.children;
  }
}

// HOC pour wrapper facilement les composants
export function withErrorBoundary<P extends object>(
  Component: React.ComponentType<P>,
  errorBoundaryProps?: Omit<ErrorBoundaryProps, 'children'>
) {
  const WrappedComponent = (props: P) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <Component {...props} />
    </ErrorBoundary>
  );

  WrappedComponent.displayName = `withErrorBoundary(${Component.displayName || Component.name})`;
  
  return WrappedComponent;
}

// Hook pour déclencher une erreur manuellement (utile pour testing)
export function useErrorHandler() {
  return (error: Error) => {
    throw error;
  };
}